// @flow

import _ from 'lodash';

import APP_ROUTES from 'client/constants/routes';

import {
  getHealthApp,
  getAppMembers,
  getEdeApp,
  getProfileIsVerified,
} from 'routes/marketplace-ede/utils/state';
import {
  getEligibleImmigrationMembers,
  getGrantDateRequiredMembers,
  getLawfulPresenceUnverifiedMembers,
  getNaturalizationUnverifiedMembers,
  getPossiblyEligibleImmigrationMembers,
  getPossiblyFosterCareMembers,
  getPossiblyFullTimeStudents,
  getPossiblyPregnantMembers,
  getPotentialNaturalizedMembers,
  getPotentialResidenceMembers,
  getPotentialVeteranMembers,
  getSevisRequiredMembers,
  isRequestingFinancialAssistance,
  memberHasEligibleImmigrationStatus,
  shouldShowChipChildrenPage,
  shouldShowPaymentHelpPage,
  shouldShowChipCoverageEndPage,
  isUnknownMarital,
  isUnknownTax,
  filterMembersRequestingCoverage,
  getNonApplicantDependents,
  getSSNMismatchMembers,
} from 'routes/marketplace-ede/utils/selectors';
import {
  ADDITIONAL_OPTIONAL_ROUTE,
  ADDITIONAL_COVERAGE_ROUTE,
  ADDITIONAL_AMERICAN_INDIAN_ALASKAN_NATIVE_ROUTE,
  CITIZENSHIP_ROUTE,
  CITIZENSHIP_VERIFY_ROUTE,
  COMPOSITION_COVERAGE_ROUTE,
  COMPOSITION_APPLICANT_ROUTE,
  FOSTER_CARE_ROUTE,
  FULL_TIME_STUDENT_ROUTE,
  GRANT_DATE_ROUTE,
  HEALTH_COVERAGE_ROUTE,
  HELP_APPLYING_ROUTE,
  ID_VERIFY_START_ROUTE,
  IMMIGRATION_CHIP_DENIAL_ROUTE,
  INCARCERATION_ROUTE,
  INCOME_START_ROUTE,
  INTRO_STATE_ROUTE,
  LAWFUL_PRESENCE_ADDITIONAL_DOCUMENTS_ROUTE,
  LAWFUL_PRESENCE_DOCUMENTS_ROUTE,
  LAWFUL_PRESENCE_ROUTE,
  LAWFUL_PRESENCE_VERIFY_ROUTE,
  MEDICAID_CHILDREN_ROUTE,
  MEDICAID_COVERAGE_END_ROUTE,
  MEDICAID_PAYMENT_HELP_ROUTE,
  NATURALIZATION_ROUTE,
  NATURALIZATION_VERIFY_ROUTE,
  PREGNANCY_ROUTE,
  QHP_APTC_SEP_ROUTE,
  RESIDENCE_ROUTE,
  SEVIS_ROUTE,
  VETERAN_ROUTE,
  TAX_COMPOSITION_ROUTE,
  TAX_SUMMARY,
  TAX_DEPENDENT_ROUTE,
  ADDITIONAL_APTC_HRA_ROUTE,
  APTC_AIAN_PAGE,
  CHIP_STATE_COVERAGE_ROUTE,
  INTRO_COVERAGE_YEAR_ROUTE,
} from 'routes/marketplace-ede/constants/routes';
import {
  membersWithoutRelationship,
  getMissingPrimaryRelationships,
} from 'routes/marketplace-ede/utils/familyRelationship';

import type { EdeApplicationPayloadType } from 'routes/marketplace-ede/types/ede/edeApplication.flow';
import type { EdeBaseRouteRequest } from 'routes/marketplace-ede/types/ede/routes.flow';
import { MINIMUM_GA_ACCESS_APP_YEAR } from '../constants/state-specific/ga';

// State-specific marketplace routes
const EDE_ROUTES_BY_STATE = {
  GA: ({ year, allowGaAccess }) =>
    (year >= MINIMUM_GA_ACCESS_APP_YEAR && allowGaAccess
      ? APP_ROUTES.GA_ACCESS_BASE_ROUTE
      : APP_ROUTES.EDE_BASE_ROUTE),
};

export const getEdeBaseRoute = ({ stateCode, year, allowGaAccess }: EdeBaseRouteRequest): string => {
  const stateFunc = EDE_ROUTES_BY_STATE[stateCode];
  return stateFunc ? stateFunc({ year, allowGaAccess }) : APP_ROUTES.EDE_BASE_ROUTE;
};

export const getEdeIntroRoute = ({ stateCode, year, allowGaAccess }: EdeBaseRouteRequest): string => {
  const baseRoute = getEdeBaseRoute({ stateCode, year, allowGaAccess });

  return `${baseRoute}/intro`;
};

export const getPathFromRoute = (route: string, basePath: string) =>
  route.replace(new RegExp(`^.*${basePath}/`), '');

// most methods assume entire store is passed as parameter
export const getNextRoute = {
  intro: ({ edeApplication }: { edeApplication: EdeApplicationPayloadType }) => {
    if (_.isEmpty(edeApplication)) {
      return INTRO_STATE_ROUTE;
    }
    return INTRO_COVERAGE_YEAR_ROUTE;
  },

  // TODO: Revisit this later when implementing household contact
  // This is the route before id verify, we haven't create ede application yet
  // Might need to check data from Profile or Product Application which replace previous ifp_app & app_json
  backgroundAddress: (state: Object) => {
    // const appID = getAppId(state);
    const isVerified = getProfileIsVerified(state);
    if (isVerified) return COMPOSITION_COVERAGE_ROUTE;

    return ID_VERIFY_START_ROUTE;
    // if (!_.get(state.ridp, 'is_verified', false)) {
    //   return ID_VERIFY_START_ROUTE;
    // }

    // const { members, app_json } = getHealthApp(state);
    // TODO: primaryNameMatchesSSNName is the local variable we saved before create edeApplication
    // Where user answer their name is not the same on the SSN
    // Refactor to use data from Product Application or Profile
    // if (
    //   members.length > 1 ||
    //   app_json.ede.primaryNameMatchesSSNName === false
    // ) {
    //   return COMPOSITION_COVERAGE_ROUTE;
    // }

    // if (app_json.ede.subsidyElection) {
    //   return CITIZENSHIP_ROUTE;
    // }

    // TODO: revisit to see how we want to handle 'primaryNameMatchesSSNName'
    // either collect it in household composition applicant edit or use the old MEMBERS_START_ROUTE to collect follow-up
    // COMPOSITION_COVERAGE_ROUTE,
    // NOTE: MEMBER_START_ROUTE has been removed since COMPOSITION_COVERAGE_ROUTE is start of flow anyways
  },
  idVerify: (state: Object, isSoloApplicant: boolean) => {
    // TODO: primaryNameMatchesSSNName is the local variable we saved before create edeApplication
    // Where user answer their name is not the same on the SSN
    // Refactor to use data from Product Application or Profile
    const { app_json } = getHealthApp(state);
    if (!isSoloApplicant || app_json.ede.primaryNameMatchesSSNName === false) {
      return COMPOSITION_COVERAGE_ROUTE;
    }

    return CITIZENSHIP_ROUTE;
  },

  memberDetails: (): string => CITIZENSHIP_ROUTE,

  citizenship: (edeApp: Object) => {
    const members = _.get(edeApp, 'edeMembers', []);

    return getSSNMismatchMembers(members).length > 0
      ? CITIZENSHIP_VERIFY_ROUTE
      : getNextRoute.citizenshipVerify(edeApp);
  },

  citizenshipVerify: (edeApp: Object) => {
    const members = _.get(edeApp, 'edeMembers', []);
    return getPotentialNaturalizedMembers(members).length > 0
      ? NATURALIZATION_ROUTE
      : getNextRoute.naturalization(edeApp);
  },

  healthCoverage: (edeApp: Object) => {
    const members = _.get(edeApp, 'edeMembers', []);
    if (shouldShowPaymentHelpPage(members)) return MEDICAID_PAYMENT_HELP_ROUTE;
    if (shouldShowChipChildrenPage(edeApp, false)) return MEDICAID_CHILDREN_ROUTE;
    if (shouldShowChipCoverageEndPage(members)) return MEDICAID_COVERAGE_END_ROUTE;
    return CHIP_STATE_COVERAGE_ROUTE;
  },

  naturalization: (edeApp: Object) => {
    const members = _.get(edeApp, 'edeMembers', []);
    return getNaturalizationUnverifiedMembers(members).length
      ? NATURALIZATION_VERIFY_ROUTE
      : getNextRoute.naturalizationVerify(edeApp);
  },

  naturalizationVerify: (edeApp: Object) => {
    const members = _.get(edeApp, 'edeMembers', []);
    return getPossiblyEligibleImmigrationMembers(members).length
      ? LAWFUL_PRESENCE_ROUTE
      : getNextRoute.lawfulPresence(edeApp);
  },

  lawfulPresence: (edeApp: Object) => {
    const members = _.get(edeApp, 'edeMembers', []);
    return getEligibleImmigrationMembers(members).length
      ? LAWFUL_PRESENCE_DOCUMENTS_ROUTE
      : getNextRoute.lawfulPresenceDocuments(edeApp);
  },

  lawfulPresenceDocuments: (edeApp: Object) => {
    const members = _.get(edeApp, 'edeMembers', []);
    return getEligibleImmigrationMembers(members).length
      ? LAWFUL_PRESENCE_ADDITIONAL_DOCUMENTS_ROUTE
      : getNextRoute.lawfulPresenceAdditionalDocuments(edeApp);
  },

  lawfulPresenceAdditionalDocuments: (edeApp: Object) => {
    const members = _.get(edeApp, 'edeMembers', []);
    return getLawfulPresenceUnverifiedMembers(members).length
      ? LAWFUL_PRESENCE_VERIFY_ROUTE
      : getNextRoute.lawfulPresenceVerify(edeApp);
  },

  lawfulPresenceVerify: (edeApp: Object) => {
    const members = _.get(edeApp, 'edeMembers', []);
    return getSevisRequiredMembers(members).length ? SEVIS_ROUTE : getNextRoute.sevis(edeApp);
  },

  sevis: (edeApp: Object) => {
    const members = _.get(edeApp, 'edeMembers', []);
    return getGrantDateRequiredMembers(members).length ? GRANT_DATE_ROUTE : getNextRoute.grantDate(edeApp);
  },

  grantDate: (edeApp: Object) => {
    const members = _.get(edeApp, 'edeMembers', []);
    return getPotentialResidenceMembers(members).length ? RESIDENCE_ROUTE : getNextRoute.residence(edeApp);
  },

  residence: (edeApp: Object) => {
    const members = _.get(edeApp, 'edeMembers', []);
    return getPotentialVeteranMembers(members).length ? VETERAN_ROUTE : getNextRoute.veteran(edeApp);
  },

  veteran: (edeApp: Object) => {
    if (isRequestingFinancialAssistance(edeApp)) return ADDITIONAL_OPTIONAL_ROUTE;
    return INCARCERATION_ROUTE;
  },

  additionalOptional: (state: Object) => {
    const edeApplication = getEdeApp(state);
    const members = getAppMembers(state);

    if (isRequestingFinancialAssistance(edeApplication)) {
      return getPossiblyPregnantMembers(members).length ? PREGNANCY_ROUTE : getNextRoute.pregnancy(state);
    }

    return INCARCERATION_ROUTE;
  },

  pregnancy: (state: Object) => {
    const members = getAppMembers(state);
    const edeApplication = getEdeApp(state);

    if (isRequestingFinancialAssistance(edeApplication)) {
      return getPossiblyFosterCareMembers(members).length
        ? FOSTER_CARE_ROUTE
        : getNextRoute.fosterCare(state);
    }

    return INCARCERATION_ROUTE;
  },

  fosterCare: (state: Object) => {
    const members = getAppMembers(state);
    const edeApplication = getEdeApp(state);

    if (isRequestingFinancialAssistance(edeApplication)) {
      return getPossiblyFullTimeStudents(members).length
        ? FULL_TIME_STUDENT_ROUTE
        : getNextRoute.fullTimeStudent();
    }

    return INCARCERATION_ROUTE;
  },

  fullTimeStudent: (): string => INCARCERATION_ROUTE,

  incarceration: (): string => ADDITIONAL_AMERICAN_INDIAN_ALASKAN_NATIVE_ROUTE,

  americanIndianAlaskanNative: (state: Object) => {
    const edeApplication = getEdeApp(state);

    if (isRequestingFinancialAssistance(edeApplication)) {
      return ADDITIONAL_COVERAGE_ROUTE;
    }

    return HELP_APPLYING_ROUTE;
  },

  helpApplying: (state: Object) => {
    const edeApplication = getEdeApp(state);

    if (isRequestingFinancialAssistance(edeApplication)) {
      return APTC_AIAN_PAGE;
    }

    return QHP_APTC_SEP_ROUTE;
  },

  additionalQuestions: () => ADDITIONAL_OPTIONAL_ROUTE,

  appSummaryHealthCoverage: (state: Object) => {
    const edeApplication = getEdeApp(state);

    if (isRequestingFinancialAssistance(edeApplication)) {
      return ADDITIONAL_COVERAGE_ROUTE;
    }

    return HEALTH_COVERAGE_ROUTE;
  },

  appSummaryHealthCoverageCont: () => ADDITIONAL_AMERICAN_INDIAN_ALASKAN_NATIVE_ROUTE,

  // TODO: Revisit these logic - it seems we check these from the updated edeApplication payload
  // Response comes from page already on subsidy path. the two routes returned
  // are those we only show to those requesting subsidy
  medicaidChipDenialFromUpdateResponse: (updatedEdeApplication: EdeApplicationPayloadType) => {
    const members = _.get(updatedEdeApplication, 'edeMembers', []);

    // TODO: Confirm which attestations qualify as "eligible immigration status"
    if (_.some(members, memberHasEligibleImmigrationStatus)) {
      return IMMIGRATION_CHIP_DENIAL_ROUTE;
    }
    return INCOME_START_ROUTE;
  },

  // covers coverage-end routing, other /medicaid sub-routes pull in either
  // route constants or use this function in conjunction with rote constants
  // res: { ede_application: {..., ede_payload, ifp_app, ...}, error: {...} }
  medicaidFromUpdateResponse: (updatedEdeApplication: EdeApplicationPayloadType) => {
    const members = _.get(updatedEdeApplication, 'edeMembers', []);

    // MEDICAID_CHILDREN_ROUTE should only appear when a child lives with 0 or 1
    // parents, and both parent and child are preliminarily Medicaid eligible
    if (shouldShowChipChildrenPage(members)) {
      return MEDICAID_CHILDREN_ROUTE;
    }
    return ADDITIONAL_APTC_HRA_ROUTE;
  },

  addressRelationships: (updatedEdeApplication: EdeApplicationPayloadType, section: string) => {
    const incompleteMembers = membersWithoutRelationship(updatedEdeApplication);

    if (section === 'tax') {
      if (incompleteMembers.length > 0) return 'household/tax/dependents';
      return 'household/tax/additional';
    }
    if (incompleteMembers.length > 0) return 'household/composition/applicants';
    return 'household/composition/relationships';
  },

  unknownTaxOrMarriage: (updatedEdeApplication: EdeApplicationPayloadType) => {
    const members = _.get(updatedEdeApplication, 'edeMembers', []);
    const newMembers = membersWithoutRelationship(updatedEdeApplication);
    const unknownTaxMembers = !!_.find(
      filterMembersRequestingCoverage(members),
      m => isUnknownMarital(m) || isUnknownTax(m),
    );
    // const claimedAsDependent = ...

    if (unknownTaxMembers || newMembers.length > 0) return TAX_COMPOSITION_ROUTE;
    // if(claimedAsDependent) return tbd parent flow;
    return TAX_SUMMARY;
  },

  tax: (state: Object, edeApplication: Object) => {
    const taxIds = _.get(state, 'attestations.pageState.taxIds', []);
    const edeApp = _.isEmpty(state) ? edeApplication : getEdeApp(state);
    const missingPrimaryRelationships = getMissingPrimaryRelationships(edeApp);
    if (!_.isEmpty(missingPrimaryRelationships)) {
      return COMPOSITION_APPLICANT_ROUTE;
    }
    if (_.isEmpty(taxIds)) {
      return _.isEmpty(getNonApplicantDependents(edeApp)) ? 'household/tax/additional' : TAX_DEPENDENT_ROUTE;
    }
    return `household/tax/composition/${taxIds[0]}`;
  },

  // taxUnclaimed: (edeApp: Object) => {
  //   const members = _.get(edeApp, 'edeMembers', []);
  //   // Consumer is requesting coverage AND requesting financial assistance AND consumer is
  //   // not listed as a tax filer filing married filing jointly AND consumer is not listed
  //   // as a tax dependent AND consumer is not a tax filer who is claiming other tax dependents
  //   const filteredMembers = _.filter(
  //     members,
  //     m => isMemberRequestingCoverage(m) && (isClaimableDependent(m) || claimedByNonApplicant(m, edeApp))
  //   );
  //   return
  // }
};
